// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme preference is one of three values:
 *
 * - `light` / `dark` — explicit user choice, persisted to localStorage
 * - `system` — track `prefers-color-scheme`, also persisted
 *
 * The provider resolves the preference to an *effective* mode (`light`
 * or `dark`) and toggles `data-theme` on `<html>`. CSS variables defined
 * in `apps/web/src/styles.css` respond to that attribute.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ThemeMode = 'light' | 'dark';

export const STORAGE_KEY = 'intakeform-theme';

interface ThemeContextValue {
  preference: ThemePreference;
  mode: ThemeMode;
  setPreference: (next: ThemePreference) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(storage: Storage | undefined): ThemePreference {
  if (storage === undefined) return 'system';
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // localStorage may be blocked (Safari private mode, etc.) — fall back to system.
  }
  return 'system';
}

function readSystemMode(window: Window | undefined): ThemeMode {
  if (window === undefined) return 'light';
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function resolveMode(preference: ThemePreference, systemMode: ThemeMode): ThemeMode {
  if (preference === 'system') return systemMode;
  return preference;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Override initial preference; primarily for tests. */
  initialPreference?: ThemePreference;
}

export function ThemeProvider({ children, initialPreference }: ThemeProviderProps) {
  const win = typeof window === 'undefined' ? undefined : window;
  const storage = typeof localStorage === 'undefined' ? undefined : localStorage;

  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initialPreference ?? readStored(storage),
  );
  const [systemMode, setSystemMode] = useState<ThemeMode>(() => readSystemMode(win));

  // Listen for system preference changes (only matters when preference === 'system').
  useEffect(() => {
    if (win === undefined) return;
    let media: MediaQueryList;
    try {
      media = win.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const handler = (e: MediaQueryListEvent): void => setSystemMode(e.matches ? 'dark' : 'light');
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [win]);

  const mode = resolveMode(preference, systemMode);

  // Apply to <html>.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset['theme'] = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const setPreference = useCallback(
    (next: ThemePreference): void => {
      setPreferenceState(next);
      try {
        storage?.setItem(STORAGE_KEY, next);
      } catch {
        // Best-effort persistence; ignore failures.
      }
    },
    [storage],
  );

  const toggle = useCallback((): void => {
    setPreference(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, mode, setPreference, toggle }),
    [preference, mode, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
